/**
 * Um documento enviado para assinatura: quem assina, em que passo cada um está e a trilha de
 * evidências que vai para o protocolo. Daqui se reenvia o link (o anterior deixa de valer), se
 * cancela o que ainda não foi concluído e se baixa o original ou o assinado.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Block as BlockIcon, Download as DownloadIcon, Send as SendIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { ActionButton, DataTable, PageShell, type DataTableColumn } from '../../components';
import assinaturaService from '../../services/assinaturaService';
import {
  COR_DO_DOCUMENTO,
  COR_DO_SIGNATARIO,
  ROTULO_DO_DOCUMENTO,
  ROTULO_DO_EVENTO,
  ROTULO_DO_SIGNATARIO,
  rotuloDaOrigem,
  type DetalheDoDocumento,
  type EventoDeAssinatura,
  type Signatario,
} from '../../types/assinatura';

function dataHora(valor: string | null): string {
  return valor ? new Date(valor).toLocaleString('pt-BR') : '—';
}

/** Em que passo o signatário está — o que a pessoa da empresa quer saber para cobrar. */
function passoDo(s: Signatario): string {
  if (s.status === 'assinado') return `Assinou em ${dataHora(s.assinadoEm)}`;
  if (s.status === 'recusado') return `Recusou: ${s.motivoRecusa ?? ''}`;
  if (s.cpfConfirmadoEm) return 'Confirmou o CPF, falta assinar';
  if (s.visualizadoEm) return `Abriu o link em ${dataHora(s.visualizadoEm)}`;
  return 'Ainda não abriu o link';
}

const colunasDeSignatarios: DataTableColumn<Signatario>[] = [
  { id: 'nome', label: 'Signatário', primary: true, render: (s) => s.nome },
  { id: 'contato', label: 'Contato', hideOnMobile: true, render: (s) => s.whatsapp ?? s.email ?? '—' },
  {
    id: 'status',
    label: 'Situação',
    render: (s) => <Chip size="small" color={COR_DO_SIGNATARIO[s.status]} label={ROTULO_DO_SIGNATARIO[s.status]} />,
  },
  { id: 'passo', label: 'Andamento', render: passoDo },
];

function detalheDoEvento(evento: EventoDeAssinatura): string | null {
  const d = evento.detalhes;
  if (!d) return null;
  const partes: string[] = [];
  if (typeof d['canal'] === 'string') partes.push(`por ${d['canal'] === 'email' ? 'e-mail' : 'WhatsApp'}`);
  if (typeof d['destino'] === 'string') partes.push(d['destino']);
  if (typeof d['motivo'] === 'string') partes.push(`motivo: ${d['motivo']}`);
  if (typeof d['arquivo'] === 'string') partes.push(`arquivo ${d['arquivo']}`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

export const AssinaturaDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const documentoId = Number(id);

  const [dados, setDados] = useState<DetalheDoDocumento | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [motivo, setMotivo] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await assinaturaService.detalhar(documentoId));
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar o documento.');
    } finally {
      setCarregando(false);
    }
  }, [documentoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const executar = useCallback(
    async (acao: () => Promise<void>, sucesso: string) => {
      setErro(null);
      setAviso(null);
      try {
        await acao();
        setAviso(sucesso);
        await carregar();
      } catch (err: unknown) {
        setErro(err instanceof Error ? err.message : 'A operação falhou.');
      }
    },
    [carregar]
  );

  const doc = dados?.documento;
  const aberto = doc?.status === 'pendente' || doc?.status === 'expirado';
  const nomeDoSignatario = new Map((doc?.signatarios ?? []).map((s) => [s.id, s.nome]));

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Assinaturas', path: '/assinaturas' }, { label: doc?.titulo ?? 'Documento' }]}
      title={doc?.titulo ?? 'Documento'}
      subtitle={doc ? `${rotuloDaOrigem(doc.origemTipo)} · enviado em ${dataHora(doc.createdAt)}` : undefined}
      actions={
        doc && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => void executar(() => assinaturaService.baixar(doc.id, 'original', `${doc.titulo}.pdf`), 'Download iniciado.')}
            >
              Original
            </Button>
            {doc.status === 'assinado' && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() =>
                  void executar(() => assinaturaService.baixar(doc.id, 'assinado', `${doc.titulo} - assinado.pdf`), 'Download iniciado.')
                }
              >
                Assinado
              </Button>
            )}
            {aberto && (
              <>
                <ActionButton
                  acao="assinatura.manage"
                  rotulo="Reenviar link"
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => void executar(() => assinaturaService.reenviar(doc.id), 'Link novo enviado a quem ainda não assinou.')}
                >
                  Reenviar link
                </ActionButton>
                <ActionButton
                  acao="assinatura.manage"
                  rotulo="Cancelar"
                  color="error"
                  variant="outlined"
                  startIcon={<BlockIcon />}
                  onClick={() => setCancelarAberto(true)}
                >
                  Cancelar
                </ActionButton>
              </>
            )}
          </Stack>
        )
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}
      {aviso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      )}

      {doc && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Situação
                </Typography>
                <Box>
                  <Chip color={COR_DO_DOCUMENTO[doc.status]} label={ROTULO_DO_DOCUMENTO[doc.status]} />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {doc.status === 'pendente' ? 'Prazo para assinar' : 'Concluído em'}
                </Typography>
                <Typography>{doc.status === 'pendente' ? dataHora(doc.expiraEm) : dataHora(doc.concluidoEm)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Código de verificação
                </Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{doc.codigoVerificacao.match(/.{1,4}/g)?.join('-')}</Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">
              SHA-256 do original
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {doc.hashOriginal}
            </Typography>
            {doc.hashFinal && (
              <>
                <Typography variant="caption" color="text.secondary">
                  SHA-256 do assinado
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {doc.hashFinal}
                </Typography>
              </>
            )}
          </Paper>

          <Typography variant="h6" gutterBottom>
            Signatários
          </Typography>
          <Box sx={{ mb: 3 }}>
            <DataTable
              columns={colunasDeSignatarios}
              rows={doc.signatarios}
              getRowId={(s) => s.id}
              emptyState={{
                title: 'Este documento não tem signatários',
                description: 'Todo documento nasce com ao menos um signatário. Se esta lista está vazia, o registro foi alterado fora do sistema.',
              }}
            />
          </Box>
        </>
      )}

      {dados && (
        <>
          <Typography variant="h6" gutterBottom>
            Trilha de evidências
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {dados.eventos.map((e) => (
                <Box key={e.id}>
                  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>
                      {ROTULO_DO_EVENTO[e.tipo] ?? e.tipo}
                    </Typography>
                    {e.signatarioId !== null && (
                      <Typography variant="body2">{nomeDoSignatario.get(e.signatarioId) ?? ''}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {dataHora(e.ocorreuEm)}
                    </Typography>
                  </Stack>
                  {(detalheDoEvento(e) || e.ip) && (
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {[detalheDoEvento(e), e.ip ? `IP ${e.ip}` : null].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </>
      )}

      {carregando && !dados && <Typography color="text.secondary">Carregando…</Typography>}

      <Dialog open={cancelarAberto} onClose={() => setCancelarAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancelar o documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Os links deixam de valer para todos os signatários. O cancelamento fica na trilha.
          </Typography>
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelarAberto(false)}>Voltar</Button>
          <Button
            color="error"
            disabled={!motivo.trim() || !doc}
            onClick={() => {
              if (!doc) return;
              setCancelarAberto(false);
              void executar(() => assinaturaService.cancelar(doc.id, motivo.trim()), 'Documento cancelado.');
            }}
          >
            Cancelar documento
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default AssinaturaDetailPage;
